/**
 * Sidebar navigation: dark menu color templates + separate "menu items" contrast styles.
 * Templates are chosen in Administration → Settings and stored in system settings.
 */

export const DEFAULT_MENU_TEMPLATE = 'midnight';
export const DEFAULT_ITEMS_TEMPLATE = 'balanced';

/** Section keys used by Sidebar.js (must match). */
export const SIDEBAR_COLOR_KEYS = [
  'general',
  'myItems',
  'myTeam',
  'okr',
  'bench',
  'help',
  'helpDesk',
  'immigration',
  'training',
  'reports',
  'emailExtractor',
  'extractedData',
  'digitalMedia',
  'admin',
];

/** Admin-facing labels for menu (section header) color templates — all use dark, saturated hues. */
export const MENU_COLOR_TEMPLATE_META = [
  { id: 'midnight', label: 'Midnight — cool slate & ink blue' },
  { id: 'forest', label: 'Forest — deep pine & teal' },
  { id: 'wine', label: 'Wine — plum, oxblood & ink' },
  { id: 'ember', label: 'Ember — charcoal, bronze & rust' },
  { id: 'mono', label: 'Graphite — near-black neutrals' },
];

/** Nested menu rows: how strongly inactive links inherit the section color. */
export const MENU_ITEMS_TEMPLATE_META = [
  { id: 'depth', label: 'Depth — nested links softer (more contrast with headers)' },
  { id: 'balanced', label: 'Balanced — default readability' },
  { id: 'lift', label: 'Lift — nested links closer to header brightness' },
];

/** Dark hex per section for each menu template. */
export const MENU_COLOR_TEMPLATES = {
  midnight: {
    general: '#0c4a6e',
    myItems: '#164e63',
    myTeam: '#3730a3',
    okr: '#831843',
    bench: '#7c2d12',
    help: '#1e3a8a',
    helpDesk: '#115e59',
    immigration: '#713f12',
    training: '#4c1d95',
    reports: '#1e3a8a',
    emailExtractor: '#854d0e',
    extractedData: '#0e7490',
    digitalMedia: '#14532d',
    admin: '#0f172a',
  },
  forest: {
    general: '#064e3b',
    myItems: '#134e4a',
    myTeam: '#14532d',
    okr: '#166534',
    bench: '#3f6212',
    help: '#1e40af',
    helpDesk: '#0f766e',
    immigration: '#365314',
    training: '#115e59',
    reports: '#065f46',
    emailExtractor: '#3d6b1f',
    extractedData: '#0f766e',
    digitalMedia: '#047857',
    admin: '#022c22',
  },
  wine: {
    general: '#581c87',
    myItems: '#701a75',
    myTeam: '#831843',
    okr: '#9f1239',
    bench: '#7f1d1d',
    help: '#312e81',
    helpDesk: '#701a75',
    immigration: '#713f12',
    training: '#4a044e',
    reports: '#4c0519',
    emailExtractor: '#7c2d12',
    extractedData: '#6b21a8',
    digitalMedia: '#831843',
    admin: '#1a0a14',
  },
  ember: {
    general: '#431407',
    myItems: '#713f12',
    myTeam: '#78350f',
    okr: '#7c2d12',
    bench: '#9a3412',
    help: '#1e3a8a',
    helpDesk: '#854d0e',
    immigration: '#713f12',
    training: '#7c2d12',
    reports: '#422006',
    emailExtractor: '#713f12',
    extractedData: '#b45309',
    digitalMedia: '#365314',
    admin: '#1c1917',
  },
  mono: {
    general: '#1e293b',
    myItems: '#334155',
    myTeam: '#0f172a',
    okr: '#475569',
    bench: '#1e293b',
    help: '#334155',
    helpDesk: '#334155',
    immigration: '#475569',
    training: '#1e293b',
    reports: '#0f172a',
    emailExtractor: '#334155',
    extractedData: '#475569',
    digitalMedia: '#475569',
    admin: '#020617',
  },
};

/** Row / nested label treatment (applied on top of menu template colors). */
export const MENU_ITEMS_STYLES = {
  depth: {
    inactiveAlpha: 0.48,
    inactiveTextAlpha: 0.82,
    rowHoverAlpha: 0.07,
    rowHoverMutedAlpha: 0.06,
    rowActiveBgAlpha: 0.2,
    rowActiveHoverAlpha: 0.26,
  },
  balanced: {
    inactiveAlpha: 0.58,
    inactiveTextAlpha: 0.88,
    rowHoverAlpha: 0.08,
    rowHoverMutedAlpha: 0.06,
    rowActiveBgAlpha: 0.18,
    rowActiveHoverAlpha: 0.24,
  },
  lift: {
    inactiveAlpha: 0.72,
    inactiveTextAlpha: 0.92,
    rowHoverAlpha: 0.1,
    rowHoverMutedAlpha: 0.08,
    rowActiveBgAlpha: 0.16,
    rowActiveHoverAlpha: 0.22,
  },
};

export function isValidMenuTemplateId(id) {
  return Boolean(id && MENU_COLOR_TEMPLATES[id]);
}

export function isValidItemsTemplateId(id) {
  return Boolean(id && MENU_ITEMS_STYLES[id]);
}

/**
 * @param {string} menuId
 * @param {string} itemsId
 * @returns {{ colors: Record<string, string>, itemStyle: object, menuId: string, itemsId: string }}
 */
export function resolveSidebarNavColors(menuId, itemsId) {
  const mid = isValidMenuTemplateId(menuId) ? menuId : DEFAULT_MENU_TEMPLATE;
  const iid = isValidItemsTemplateId(itemsId) ? itemsId : DEFAULT_ITEMS_TEMPLATE;
  return {
    colors: MENU_COLOR_TEMPLATES[mid],
    itemStyle: MENU_ITEMS_STYLES[iid],
    menuId: mid,
    itemsId: iid,
  };
}
